import React, { useState, useEffect, useRef, useCallback } from 'react';
import { videoAPI } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { toast } from 'sonner';
import {
  Heart, Download, Sparkles, ArrowLeft, Loader2, Eye,
  Play, Pause, Volume2, VolumeX, ChevronUp, ChevronDown, Share2
} from 'lucide-react';

const VideoGallery = ({ onBack, onGenerateSimilar }) => {
  const { user } = useAuth();
  const [videos, setVideos] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [shareMenuId, setShareMenuId] = useState(null);
  const containerRef = useRef(null);
  const observerRef = useRef(null);

  const loadVideos = useCallback(async (pageNum = 1, append = false) => {
    if (loading) return;
    setLoading(true);
    try {
      const res = await videoAPI.getGallery(pageNum, 10);
      const data = res.data;
      setVideos(prev => append ? [...prev, ...data.videos] : data.videos);
      setHasMore(data.has_more);
      setPage(pageNum);
    } catch { /* ignore */ }
    setLoading(false);
  }, [loading]);

  useEffect(() => { loadVideos(1); }, []);

  // Infinite scroll trigger
  const lastVideoRef = useCallback(node => {
    if (loading) return;
    if (observerRef.current) observerRef.current.disconnect();
    observerRef.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        loadVideos(page + 1, true);
      }
    });
    if (node) observerRef.current.observe(node);
  }, [loading, hasMore, page, loadVideos]);

  const handleLike = async (videoId) => {
    try {
      const res = await videoAPI.toggleLike(videoId);
      setVideos(prev => prev.map(v =>
        v.id === videoId
          ? { ...v, like_count: res.data.like_count, likes: res.data.liked ? [...(v.likes || []), user?.id] : (v.likes || []).filter(id => id !== user?.id) }
          : v
      ));
    } catch { toast.error('Failed to like'); }
  };

  const handleDownload = (url, prompt) => {
    const a = document.createElement('a');
    a.href = url;
    a.download = `callchat-${prompt.slice(0, 20).replace(/\s+/g, '-')}.mp4`;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    a.click();
  };

  const handleGalleryShare = async (video, platform) => {
    setShareMenuId(null);
    const shareText = `"${video.prompt.slice(0, 80)}" - Made with CallChat AI`;
    const shareUrl = video.video_url;

    if (platform === 'native' && navigator.share) {
      try {
        await navigator.share({ title: 'CallChat AI Video', text: shareText, url: shareUrl });
      } catch (e) { if (e.name !== 'AbortError') toast.error('Share failed'); }
      return;
    }
    if (platform === 'whatsapp') {
      window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(shareText + '\n\n' + shareUrl)}`, '_blank');
    } else if (platform === 'facebook') {
      window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, '_blank');
    } else if (platform === 'instagram') {
      handleDownload(shareUrl, video.prompt);
      toast.success('Video downloaded! Upload to Instagram from your gallery.');
    } else if (platform === 'copy') {
      try { await navigator.clipboard.writeText(shareUrl); toast.success('Link copied!'); } catch { toast.error('Copy failed'); }
    }
  };

  return (
    <div className="flex flex-col h-full bg-[#0F172A]" data-testid="video-gallery">
      {/* Header */}
      <div className="sticky top-0 z-20 flex items-center gap-3 px-4 py-3 bg-[#0F172A]/90 backdrop-blur-lg border-b border-white/10">
        <Button variant="ghost" size="icon" onClick={onBack} className="text-white hover:bg-white/10" data-testid="gallery-back">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="font-bold text-white text-lg leading-tight">Explore</h1>
          <p className="text-xs text-[#94A3B8]">AI-generated videos</p>
        </div>
      </div>

      {/* Video Feed */}
      <div ref={containerRef} className="flex-1 overflow-y-auto snap-y snap-mandatory" style={{ scrollSnapType: 'y mandatory' }}>
        {videos.length === 0 && !loading && (
          <div className="flex flex-col items-center justify-center h-full text-center px-8">
            <Sparkles className="w-12 h-12 text-[#7C3AED] mb-4" />
            <p className="text-white text-lg font-medium mb-2">No videos yet</p>
            <p className="text-[#94A3B8] text-sm">Be the first to generate and share a video!</p>
          </div>
        )}
        {videos.map((video, index) => {
          const isLiked = (video.likes || []).includes(user?.id);
          const isLast = index === videos.length - 1;
          return (
            <div
              key={video.id}
              ref={isLast ? lastVideoRef : null}
              className="snap-start relative w-full bg-black flex items-center justify-center"
              style={{ height: 'calc(100vh - 56px)', minHeight: '500px' }}
              data-testid={`gallery-video-${video.id}`}
            >
              {/* Video Player */}
              <VideoPlayer video={video} isActive={index === activeIndex} onVisible={() => setActiveIndex(index)} />

              {/* Watermark overlay for free-tier videos */}
              {video.has_watermark && (
                <div className="absolute top-4 left-4 px-2.5 py-1 bg-black/40 backdrop-blur-sm rounded-lg text-[11px] text-white/60 font-semibold tracking-wide pointer-events-none select-none">
                  CallChat AI
                </div>
              )}

              {/* Overlay: Bottom info */}
              <div className="absolute bottom-0 left-0 right-16 p-4 bg-gradient-to-t from-black/80 via-black/40 to-transparent pointer-events-none">
                <p className="text-white font-semibold text-sm mb-1">{video.user_name || 'Anonymous'}</p>
                <p className="text-white/80 text-xs line-clamp-2">{video.prompt}</p>
                {video.template && (
                  <span className="inline-block mt-1.5 text-[10px] font-medium text-white/70 bg-white/15 px-2 py-0.5 rounded-full">
                    {video.template}
                  </span>
                )}
              </div>

              {/* Overlay: Right actions */}
              <div className="absolute right-3 bottom-20 flex flex-col items-center gap-5">
                <button
                  onClick={() => handleLike(video.id)}
                  className="flex flex-col items-center gap-1 group"
                  data-testid={`like-btn-${video.id}`}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                    isLiked ? 'bg-red-500 scale-110' : 'bg-white/15 group-hover:bg-white/25'
                  }`}>
                    <Heart className={`w-5 h-5 ${isLiked ? 'text-white fill-white' : 'text-white'}`} />
                  </div>
                  <span className="text-white text-xs font-medium">{video.like_count || 0}</span>
                </button>

                <button
                  onClick={() => onGenerateSimilar && onGenerateSimilar(video.prompt, video.template)}
                  className="flex flex-col items-center gap-1 group"
                  data-testid={`similar-btn-${video.id}`}
                >
                  <div className="w-10 h-10 rounded-full bg-white/15 group-hover:bg-white/25 flex items-center justify-center transition-all">
                    <Sparkles className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-white text-[10px]">Similar</span>
                </button>

                <button
                  onClick={() => handleDownload(video.video_url, video.prompt)}
                  className="flex flex-col items-center gap-1 group"
                  data-testid={`download-btn-${video.id}`}
                >
                  <div className="w-10 h-10 rounded-full bg-white/15 group-hover:bg-white/25 flex items-center justify-center transition-all">
                    <Download className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-white text-[10px]">Save</span>
                </button>

                <div className="relative">
                  <button
                    onClick={() => setShareMenuId(shareMenuId === video.id ? null : video.id)}
                    className="flex flex-col items-center gap-1 group"
                    data-testid={`share-btn-${video.id}`}
                  >
                    <div className="w-10 h-10 rounded-full bg-white/15 group-hover:bg-white/25 flex items-center justify-center transition-all">
                      <Share2 className="w-5 h-5 text-white" />
                    </div>
                    <span className="text-white text-[10px]">Share</span>
                  </button>
                  {shareMenuId === video.id && (
                    <div className="absolute right-0 bottom-full mb-2 bg-[#1E293B] border border-white/10 rounded-xl shadow-2xl p-2 z-50 min-w-[160px]" data-testid={`gallery-share-menu-${video.id}`}>
                      <button onClick={() => handleGalleryShare(video, 'whatsapp')} className="flex items-center gap-2 w-full px-3 py-2 text-sm text-white hover:bg-white/10 rounded-lg transition-colors" data-testid="gallery-share-whatsapp">
                        <svg className="w-4 h-4 text-[#25D366]" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                        WhatsApp
                      </button>
                      <button onClick={() => handleGalleryShare(video, 'facebook')} className="flex items-center gap-2 w-full px-3 py-2 text-sm text-white hover:bg-white/10 rounded-lg transition-colors" data-testid="gallery-share-facebook">
                        <svg className="w-4 h-4 text-[#1877F2]" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                        Facebook
                      </button>
                      <button onClick={() => handleGalleryShare(video, 'instagram')} className="flex items-center gap-2 w-full px-3 py-2 text-sm text-white hover:bg-white/10 rounded-lg transition-colors" data-testid="gallery-share-instagram">
                        <svg className="w-4 h-4 text-[#E4405F]" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678a6.162 6.162 0 100 12.324 6.162 6.162 0 100-12.324zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405a1.441 1.441 0 11-2.882 0 1.441 1.441 0 012.882 0z"/></svg>
                        Instagram
                      </button>
                      <div className="border-t border-white/10 my-1"></div>
                      {navigator.share && (
                        <button onClick={() => handleGalleryShare(video, 'native')} className="flex items-center gap-2 w-full px-3 py-2 text-sm text-white hover:bg-white/10 rounded-lg transition-colors" data-testid="gallery-share-native">
                          <Share2 className="w-4 h-4 text-[#7C3AED]" /> More
                        </button>
                      )}
                      <button onClick={() => handleGalleryShare(video, 'copy')} className="flex items-center gap-2 w-full px-3 py-2 text-sm text-white hover:bg-white/10 rounded-lg transition-colors" data-testid="gallery-share-copy">
                        <svg className="w-4 h-4 text-[#94A3B8]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>
                        Copy Link
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}

        {loading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-8 h-8 text-[#7C3AED] animate-spin" />
          </div>
        )}
      </div>
    </div>
  );
};

// Individual video player with play/pause and mute
const VideoPlayer = ({ video, isActive, onVisible }) => {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const observerRef = useRef(null);

  // Auto-play when visible, pause when not
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    observerRef.current = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          onVisible?.();
          el.play().then(() => setPlaying(true)).catch(() => {});
        } else {
          el.pause();
          setPlaying(false);
        }
      },
      { threshold: 0.6 }
    );
    observerRef.current.observe(el);
    return () => observerRef.current?.disconnect();
  }, [onVisible]);

  const togglePlay = () => {
    const el = videoRef.current;
    if (!el) return;
    if (el.paused) {
      el.play().then(() => setPlaying(true)).catch(() => {});
    } else {
      el.pause();
      setPlaying(false);
    }
  };

  return (
    <div className="relative w-full h-full flex items-center justify-center" onClick={togglePlay}>
      <video
        ref={videoRef}
        src={video.video_url}
        className="w-full h-full object-contain"
        loop
        muted={muted}
        playsInline
        preload="metadata"
        data-testid={`video-element-${video.id}`}
      />
      {/* Play/Pause indicator */}
      {!playing && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-16 h-16 rounded-full bg-black/40 flex items-center justify-center">
            <Play className="w-8 h-8 text-white ml-1" />
          </div>
        </div>
      )}
      {/* Mute toggle */}
      <button
        onClick={e => { e.stopPropagation(); setMuted(!muted); }}
        className="absolute top-4 right-4 w-8 h-8 rounded-full bg-black/40 flex items-center justify-center"
        data-testid={`mute-btn-${video.id}`}
      >
        {muted ? <VolumeX className="w-4 h-4 text-white" /> : <Volume2 className="w-4 h-4 text-white" />}
      </button>
    </div>
  );
};

export default VideoGallery;
